import { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RotateCcw } from 'lucide-react';
import itemsData from '../character/data/items.json';
import ItemSheet, { ItemLink, ItemLinkTarget, openOnClick } from '../components/ItemSheet';
import consumablesData from '../character/data/consumables.json';
import { fuzzyFilter } from '../search/fuzzySearch';
import { parseTextWithTerms } from '../character/content/parseText';
import { useIsMobile } from '../hooks/useIsMobile';
import { useSort } from '../hooks/useSort';
import { useDarkMode } from '../character/useDarkMode';
import { useStrings } from '../locales';
import { SortIcon, SortState } from '../components/SortIcon';
import { ColorEntry, RARITY_COLORS, RARITY_COLORS_LIGHT } from '../character/content/rarity';
import './Items.css';

/* ── Types ───────────────────────────────────────────────────────── */

interface LootRow {
  roll: number;
  name: string;
  rarity: string;
  subtype?: string;
  category?: string;
  text: string;
  [key: string]: string | number | undefined;
}

/* ── Subtype / type colours ─────────────────────────────────────── */
const SUBTYPE_COLORS: Record<string, ColorEntry> = {
  General: { bg: 'rgba(168,156,200,0.12)', color: '#a89cc8' },
  Relic: { bg: 'rgba(201,162,39,0.14)', color: '#e8c547' },
  'Weapon Attachment': { bg: 'rgba(192,64,64,0.14)', color: '#ff7070' },
  'Armor Attachment': { bg: 'rgba(74,158,255,0.14)', color: '#6aaeff' },
  Recipe: { bg: 'rgba(96,216,144,0.14)', color: '#60d890' },
};
const SUBTYPE_COLORS_LIGHT: Record<string, ColorEntry> = {
  General: { bg: 'rgba(91,63,138,0.1)', color: '#5b3f8a' },
  Relic: { bg: 'rgba(146,64,14,0.1)', color: '#92400e' },
  'Weapon Attachment': { bg: 'rgba(153,27,27,0.1)', color: '#991b1b' },
  'Armor Attachment': { bg: 'rgba(29,78,216,0.1)', color: '#1d4ed8' },
  Recipe: { bg: 'rgba(22,101,52,0.1)', color: '#166534' },
};

const CATEGORY_COLORS: Record<string, ColorEntry> = {
  Healing: { bg: 'rgba(96,216,144,0.14)', color: '#60d890' },
  Enhancement: { bg: 'rgba(201,162,39,0.14)', color: '#e8c547' },
  Combat: { bg: 'rgba(192,64,64,0.14)', color: '#ff7070' },
  Utility: { bg: 'rgba(168,156,200,0.12)', color: '#a89cc8' },
};
const CATEGORY_COLORS_LIGHT: Record<string, ColorEntry> = {
  Healing: { bg: 'rgba(22,101,52,0.1)', color: '#166534' },
  Enhancement: { bg: 'rgba(146,64,14,0.1)', color: '#92400e' },
  Combat: { bg: 'rgba(153,27,27,0.1)', color: '#991b1b' },
  Utility: { bg: 'rgba(91,63,138,0.1)', color: '#5b3f8a' },
};

// const ALL_RARITIES = ['All', 'Common', 'Uncommon', 'Rare', 'Legendary'];
const ALL_SUBTYPES = ['All', 'General', 'Relic', 'Weapon Attachment', 'Armor Attachment', 'Recipe'];
const ALL_CATEGORIES = ['All', 'Healing', 'Enhancement', 'Combat', 'Utility'];

const SORT_COLS_ITEMS = [
  { col: 'name', label: 'Name' },
  { col: 'subtype', label: 'Type' },
];
const SORT_COLS_CONS = [
  { col: 'name', label: 'Name' },
  { col: 'category', label: 'Type' },
];

/* ── Multi-select helpers ────────────────────────────────────────── */
function toggleFilter(current: Set<string>, value: string): Set<string> {
  if (value === 'All') return new Set(['All']);
  const next = new Set(current);
  next.delete('All');
  if (next.has(value)) {
    next.delete(value);
    if (next.size === 0) next.add('All');
  } else {
    next.add(value);
  }
  return next;
}

function passesFilter(set: Set<string>, value: string): boolean {
  return set.has('All') || set.has(value);
}

function isDefault(s: Set<string>): boolean {
  return s.size === 1 && s.has('All');
}

/* ── Sort Icon ───────────────────────────────────────────────────── */

/* ── Reset button ────────────────────────────────────────────────── */
function ResetBtn({ dirty, onClick }: { dirty: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={!dirty}
      title="Reset all filters"
      className={`reset-btn ${dirty ? 'dirty' : ''}`}
    >
      <RotateCcw size={13} /> Reset
    </button>
  );
}

/* ── Vertical pill filter ────────────────────────────────────────── */
function VerticalFilter({ label, options, filter, onToggle, colorMap }: {
  label: string;
  options: string[];
  filter: Set<string>;
  onToggle: (next: Set<string>) => void;
  colorMap?: Record<string, ColorEntry>;
}) {
  return (
    <div className="vertical-filter">
      <div className="filter-label">{label}</div>
      <div className="filter-pill-group">
        {options.map((opt) => {
          const active = filter.has(opt);
          const accent = colorMap?.[opt]?.color;
          return (
            <button
              key={opt}
              onClick={() => onToggle(toggleFilter(filter, opt))}
              className="filter-pill"
              aria-pressed={active}
              style={{
                background: active ? (accent ? `${accent}22` : 'var(--gold-bg)') : 'transparent',
                border: active ? `1px solid ${accent || 'var(--gold)'}` : `1px solid ${accent ? `${accent}44` : 'var(--border)'}`,
                color: active ? (accent || 'var(--gold)') : accent ? `${accent}cc` : 'var(--text-secondary)',
              }}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Small badge ─────────────────────────────────────────────────── */
function Badge({ label, colorObj }: { label: string; colorObj?: ColorEntry }) {
  const c = colorObj || { bg: 'var(--bg-card)', color: 'var(--text-muted)' };
  return (
    <span
      className="badge"
      style={{
        background: c.bg,
        color: c.color,
        border: `1px solid ${c.color}55`
      }}
    >
      {label}
    </span>
  );
}

/* ── Item text with glossary term linking ────────────────────────── */
function ItemText({ text }: { text: string }) {
  return <span>{parseTextWithTerms(text)}</span>;
}

/* ── Mobile loot card ────────────────────────────────────────────── */
function LootCard({ item, typeKey, typeColors, link }: {
  item: LootRow;
  typeKey: string;
  typeColors: Record<string, ColorEntry>;
  link?: ItemLinkTarget;
}) {
  const typeVal = String(item[typeKey] ?? '');
  return (
    <div className={`loot-card${link ? ' item-row' : ''}`} onClick={link && openOnClick(link.onOpen)}>
      <div className="loot-card-header">
        <span className="loot-card-name">{link ? <ItemLink {...link}>{item.name}</ItemLink> : item.name}</span>
        <Badge label={typeVal} colorObj={typeColors[typeVal]} />
      </div>
      <div className="loot-card-roll">#{String(item.roll).padStart(2, '0')}</div>
      {item.text && (
        <div className="loot-card-effect">
          <ItemText text={item.text} />
        </div>
      )}
    </div>
  );
}

/* ── Generic sortable table ──────────────────────────────────────── */
function LootTable({ rows, sort, onSort, typeKey, typeColors, typeLabel, isMobile, linkOf }: {
  rows: LootRow[];
  linkOf: (item: LootRow) => ItemLinkTarget;
  sort: SortState;
  onSort: (col: string) => void;
  typeKey: string;
  typeColors: Record<string, ColorEntry>;
  typeLabel: string;
  sortCols: { col: string; label: string }[];
  isMobile?: boolean;
}) {
  if (isMobile) {
    return (
      <div className="loot-card-list">
        {rows.map((item) => (
          <LootCard key={item.name + item.roll} item={item} typeKey={typeKey} typeColors={typeColors} link={linkOf(item)} />
        ))}
      </div>
    );
  }

  return (
    <div className="equipment-table-wrap">
      <table className="equipment-table">
        <thead>
          <tr>
            <th
              onClick={() => onSort('roll')}
              className="sortable-header roll-header"
              aria-sort={sort.col === 'roll' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
            >
              #<SortIcon col="roll" sort={sort} />
            </th>
            <th
              onClick={() => onSort('name')}
              className="sortable-header name-header"
              aria-sort={sort.col === 'name' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
            >
              Name<SortIcon col="name" sort={sort} />
            </th>
            <th
              className="sortable-header"
              onClick={() => onSort(typeKey)}
              aria-sort={sort.col === typeKey ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
            >
              {typeLabel}<SortIcon col={typeKey} sort={sort} />
            </th>
            <th
              className="sortable-header effect-header"
              onClick={() => onSort('text')}
              aria-sort={sort.col === 'text' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
            >
              Effect<SortIcon col="text" sort={sort} />
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((item) => (
            <tr key={item.name + item.roll} className="item-row" onClick={openOnClick(linkOf(item).onOpen)}>
              <td className="roll-number">
                {String(item.roll).padStart(2, '0')}
              </td>
              <td className="item-name">
                <ItemLink {...linkOf(item)}>{item.name}</ItemLink>
              </td>
              <td>
                <Badge label={String(item[typeKey] ?? '')} colorObj={typeColors[String(item[typeKey] ?? '')]} />
              </td>
              <td className="item-effect">
                <ItemText text={item.text} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Sort helper ─────────────────────────────────────────────────── */
function sortLoot(list: LootRow[], sort: SortState, typeKey: string): LootRow[] {
  if (!sort.col) return list;
  const d = sort.dir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    switch (sort.col) {
      case 'roll': return d * ((a.roll as number) - (b.roll as number));
      case 'name': return d * (a.name as string).localeCompare(b.name as string);
      case typeKey: return d * (String(a[typeKey] || '')).localeCompare(String(b[typeKey] || '')) || (a.roll as number) - (b.roll as number);
      default: return 0;
    }
  });
}

/* ── Filter Chip Component ───────────────────────────────────────── */
function FilterChip({ label, color, onRemove }: { label: string; color?: string; onRemove: () => void }) {
  const chipColor = color || 'var(--gold)';
  return (
    <span
      className="filter-chip"
      style={{
        background: `${chipColor}18`,
        border: `1px solid ${chipColor}44`,
        color: chipColor
      }}
    >
      {label}
      <button
        onClick={onRemove}
        className="filter-chip-remove"
        style={{ color: chipColor }}
      >
        ×
      </button>
    </span>
  );
}

/* ════════════════════════════════════════════════════════════════
   Main page
   ════════════════════════════════════════════════════════════════ */
export default function Items() {
  const s = useStrings().items;
  const [searchParams, setSearchParams] = useSearchParams();
  const initTab = searchParams.get('tab') === 'consumables' ? 'consumables' : 'items';
  const initQ = searchParams.get('q') || '';

  const isMobile = useIsMobile(1175);
  const dark = useDarkMode();
  const rarityColors = dark ? RARITY_COLORS : RARITY_COLORS_LIGHT;
  const subtypeColors = dark ? SUBTYPE_COLORS : SUBTYPE_COLORS_LIGHT;
  const catColors = dark ? CATEGORY_COLORS : CATEGORY_COLORS_LIGHT;

  const [showFilters, setShowFilters] = useState(false);
  const [tab, setTab] = useState(initTab);

  /* Item sheet: an item's link (?tab=...&item=Name) opens its shareable sheet */
  const [openItem, setOpenItem] = useState(() => searchParams.get('item'));
  const itemHref = (t: string, name: string) => `/items?tab=${t}&item=${encodeURIComponent(name)}`;
  const linkOf = (t: string) => (item: LootRow): ItemLinkTarget => ({
    href: itemHref(t, item.name),
    onOpen: () => { setOpenItem(item.name); setSearchParams({ tab: t, item: item.name }, { replace: true }); },
  });
  const closeItem = useCallback(() => { setOpenItem(null); setSearchParams({ tab }, { replace: true }); }, [tab, setSearchParams]);
  const openRow = ((tab === 'items' ? itemsData : consumablesData) as LootRow[]).find((r) => r.name === openItem);

  /* ── Items state ── */
  const [iSearch, setISearch] = useState(initTab === 'items' ? initQ : '');
  const [iRarity, setIRarity] = useState(new Set(['All']));
  const [iSubtype, setISubtype] = useState(new Set(['All']));
  const [iSort, handleISort, resetISort] = useSort({ col: '', dir: 'asc' });

  /* ── Consumables state ── */
  const [cSearch, setCSearch] = useState(initTab === 'consumables' ? initQ : '');
  const [cCat, setCCat] = useState(new Set(['All']));
  const [cSort, handleCSort, resetCSort] = useSort({ col: '', dir: 'asc' });

  /* ── Dirty / reset ── */
  const iDirty = !!iSearch || !isDefault(iRarity) || !isDefault(iSubtype) || iSort.col !== '';
  const cDirty = !!cSearch || !isDefault(cCat) || cSort.col !== '';

  const resetItems = () => {
    setISearch('');
    setIRarity(new Set(['All']));
    setISubtype(new Set(['All']));
    resetISort();
  };

  const resetCons = () => {
    setCSearch('');
    setCCat(new Set(['All']));
    resetCSort();
  };

  /* ── Filtered & sorted items ── */
  const filteredItems = useMemo(() => {
    const base = (itemsData as LootRow[]).filter((i) =>
      passesFilter(iRarity, i.rarity) && passesFilter(iSubtype, i.subtype ?? '')
    );
    const searched = fuzzyFilter(base, iSearch, (i) => i.name, (i) => i.text);
    if (iSearch.trim()) return searched;
    return sortLoot(searched, iSort, 'subtype');
  }, [iRarity, iSubtype, iSearch, iSort]);

  /* ── Filtered & sorted consumables ── */
  const filteredCons = useMemo(() => {
    const base = (consumablesData as LootRow[]).filter((c) =>
      passesFilter(cCat, c.category ?? '')
    );
    const searched = fuzzyFilter(base, cSearch, (c) => c.name, (c) => c.text);
    if (cSearch.trim()) return searched;
    return sortLoot(searched, cSort, 'category');
  }, [cCat, cSearch, cSort]);

  /* ── Render filter chips helper ── */
  const renderFilterChips = (
    search: string,
    raritySet: Set<string>,
    typeSet: Set<string>,
    onSearchClear: (val: string) => void,
    onRarityToggle: (updater: (p: Set<string>) => Set<string>) => void,
    onTypeToggle: (updater: (p: Set<string>) => Set<string>) => void,
    rarityColors: Record<string, ColorEntry>,
    typeColors: Record<string, ColorEntry>,
  ) => {
    const chips = [
      ...(search ? [{ label: `"${search}"`, color: 'var(--gold)', onRemove: () => onSearchClear('') }] : []),
      ...[...raritySet].filter(v => v !== 'All').map(v => ({
        label: v,
        color: rarityColors[v]?.color,
        onRemove: () => onRarityToggle(p => toggleFilter(p, v))
      })),
      ...[...typeSet].filter(v => v !== 'All').map(v => ({
        label: v,
        color: typeColors[v]?.color,
        onRemove: () => onTypeToggle(p => toggleFilter(p, v))
      })),
    ];

    return chips.length > 0 ? (
      <div className="mobile-filter-chips">
        {chips.map(({ label, color, onRemove }) => (
          <FilterChip key={label} label={label} color={color} onRemove={onRemove} />
        ))}
      </div>
    ) : null;
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>{s.title}</h1>
        <p>
          Magical gear, relics, crafting recipes, and single-use consumables from the Daggerheart SRD.
          Items are sorted by their loot table roll number, lower rolls are more common, higher rolls are rarer.
          {' '}{itemsData.length} items · {consumablesData.length} consumables.
        </p>
      </div>

      {/* Tab bar */}
      <div className="tab-bar" role="tablist">
        {[
          { id: 'items', label: s.tabs.items(itemsData.length) },
          { id: 'consumables', label: s.tabs.consumables(consumablesData.length) },
        ].map((t) => {
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              id={`tab-${t.id}`}
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              className={`tab-btn ${isActive ? 'active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Loot & Rarity info card */}
      <div className="info-card">
        <p>
          Loot comprises any consumables or reusable items the party acquires. Items can be used until sold, discarded, or lost. To generate a random item, choose a rarity, roll the designated dice, and match the total to the item in the table.
        </p>
        <div className="dice-grid">
          {[
            { r: 'Common', dice: '1d12 or 2d12' },
            { r: 'Uncommon', dice: '2d12 or 3d12' },
            { r: 'Rare', dice: '3d12 or 4d12' },
            { r: 'Legendary', dice: '4d12 or 5d12' },
          ].map(({ r, dice }) => {
            const c = rarityColors[r];
            return (
              <div key={r} className="dice-item">
                <span
                  className="rarity-badge"
                  style={{
                    background: c.bg,
                    color: c.color,
                    border: `1px solid ${c.color}44`
                  }}
                >
                  {r}
                </span>
                <span className="dice-text">{dice}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ════════ ITEMS ════════ */}
      {tab === 'items' && (
        <div role="tabpanel" aria-labelledby="tab-items">
          <input
            type="text"
            id="items-search"
            name="items-search"
            placeholder={s.items.searchPlaceholder}
            value={iSearch}
            onChange={(e) => setISearch(e.target.value)}
            className="search-input filter-search-bar"
            aria-label={s.items.searchAria}
          />
          <div className="filter-layout">
            {/* Mobile filter toggle row */}
            <div className="filter-toggle-row">
              <button className="filter-toggle-btn" onClick={() => setShowFilters(f => !f)}>
                <span>⚙ Filters{iDirty ? ' •' : ''}</span>
                <span>{showFilters ? '▲' : '▼'}</span>
              </button>
              <ResetBtn dirty={iDirty} onClick={resetItems} />
            </div>

            {/* Mobile active filter chips */}
            {renderFilterChips(
              iSearch, iRarity, iSubtype,
              setISearch, setIRarity, setISubtype,
              rarityColors, subtypeColors
            )}

            {/* Left panel */}
            <div className={`filter-sidebar${showFilters ? '' : ' filter-sidebar--hidden'}`}>
              <div className="filter-sidebar-header">
                <span className="filter-label">Filters</span>
                <span className="hide-mobile"><ResetBtn dirty={iDirty} onClick={resetItems} /></span>
              </div>
              <VerticalFilter
                label="Type"
                options={ALL_SUBTYPES}
                filter={iSubtype}
                onToggle={setISubtype}
                colorMap={subtypeColors}
              />
              <div className="filter-stats">
                {filteredItems.length} of {itemsData.length} items
              </div>
            </div>

            {/* Right panel */}
            <div className="filter-right-panel">
              {filteredItems.length === 0 && (
                <div className="empty-state">
                  <p>{s.items.noMatch}</p>
                </div>
              )}

              <LootTable
                rows={filteredItems}
                sort={iSort}
                onSort={handleISort}
                typeKey="subtype"
                typeColors={subtypeColors}
                typeLabel="Type"
                sortCols={SORT_COLS_ITEMS}
                isMobile={isMobile}
                linkOf={linkOf('items')}
              />

              <div className="rules-section">
                <h3 className="rules-title">Item Rules</h3>
                <div className="rules-grid">
                  {[
                    { k: 'Relics', v: 'Grant a permanent +1 bonus to a trait. You can only carry one relic at a time.' },
                    { k: 'Weapon Attachments', v: 'Attach directly to a weapon. The weapon retains the attachment until it is removed.' },
                    { k: 'Armor Attachments', v: 'Attach to armor that does not already have a feature.' },
                    { k: 'Recipes', v: 'Crafted as a downtime move, each recipe specifies what ingredients are required.' },
                    { k: 'Loot Table', v: 'Roll the listed dice and match the total. Lower totals = common finds, higher = legendary.' },
                  ].map(({ k, v }) => (
                    <div key={k} className="rule-item">
                      <strong>{k}:</strong> {v}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ════════ CONSUMABLES ════════ */}
      {tab === 'consumables' && (
        <div role="tabpanel" aria-labelledby="tab-consumables">
          <input
            type="text"
            id="consumables-search"
            name="items-consumables-search"
            placeholder={s.consumables.searchPlaceholder}
            value={cSearch}
            onChange={(e) => setCSearch(e.target.value)}
            className="search-input filter-search-bar"
            aria-label={s.consumables.searchAria}
          />
          <div className="filter-layout">
            {/* Mobile filter toggle row */}
            <div className="filter-toggle-row">
              <button className="filter-toggle-btn" onClick={() => setShowFilters(f => !f)}>
                <span>⚙ Filters{cDirty ? ' •' : ''}</span>
                <span>{showFilters ? '▲' : '▼'}</span>
              </button>
              <ResetBtn dirty={cDirty} onClick={resetCons} />
            </div>

            {/* Mobile active filter chips */}
            {renderFilterChips(
              cSearch, new Set(['All']), cCat,
              setCSearch, () => { }, setCCat,
              rarityColors, catColors
            )}

            {/* Left panel */}
            <div className={`filter-sidebar${showFilters ? '' : ' filter-sidebar--hidden'}`}>
              <div className="filter-sidebar-header">
                <span className="filter-label">Filters</span>
                <span className="hide-mobile"><ResetBtn dirty={cDirty} onClick={resetCons} /></span>
              </div>
              <VerticalFilter
                label="Type"
                options={ALL_CATEGORIES}
                filter={cCat}
                onToggle={setCCat}
                colorMap={catColors}
              />
              <div className="filter-stats">
                {filteredCons.length} of {consumablesData.length} consumables
              </div>
            </div>

            {/* Right panel */}
            <div className="filter-right-panel">
              {filteredCons.length === 0 && (
                <div className="empty-state">
                  <p>{s.consumables.noMatch}</p>
                </div>
              )}

              <LootTable
                rows={filteredCons}
                sort={cSort}
                onSort={handleCSort}
                typeKey="category"
                typeColors={catColors}
                typeLabel="Type"
                sortCols={SORT_COLS_CONS}
                isMobile={isMobile}
                linkOf={linkOf('consumables')}
              />

              <div className="rules-section">
                <h3 className="rules-title">Consumable Rules</h3>
                <div className="rules-grid">
                  {[
                    { k: 'Single Use', v: 'Consumables are destroyed after use.' },
                    { k: 'Stack Limit', v: 'You can hold up to five of each consumable at a time.' },
                    { k: 'No Roll', v: 'Using a consumable does not require a roll unless stated or demanded by the fiction.' },
                    { k: 'Loot Table', v: 'Common 1d12/2d12 · Uncommon 2d12/3d12 · Rare 3d12/4d12 · Legendary 4d12/5d12.' },
                  ].map(({ k, v }) => (
                    <div key={k} className="rule-item">
                      <strong>{k}:</strong> {v}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {openRow && (
        <ItemSheet
          title={openRow.name}
          kind={`${openRow.rarity} ${tab === 'items' ? 'Item' : 'Consumable'}`}
          stats={[[
            { label: 'Roll', value: String(openRow.roll).padStart(2, '0') },
            { label: 'Type', value: String((tab === 'items' ? openRow.subtype : openRow.category) ?? '') },
          ]]}
          href={itemHref(tab, openRow.name)}
          onClose={closeItem}
        >
          <p><ItemText text={openRow.text} /></p>
        </ItemSheet>
      )}
    </div>
  );
}
